/**
 * StatGroupRenderer — stat-group | stats
 * KPI metrics grid (1–4 stats). Inlines stat layout to support EditableText + animation.
 */
import React from "react";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { Title } from "../../ui/Title";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional } from "../../utils/resolveText";
import { SortableList } from "../SortableList";

/* eslint-disable react/prop-types */

export function StatGroupRenderer({
  element,
  editing,
  onChange,
  onReorder,
  playing,
  isPaused,
  currentTimeSec,
}) {
  const stats = element.stats || [];
  const title = element.title ? resolveText(element.title) : null;
  const cols = Math.min(stats.length, 4);
  // Element-level animation wraps the whole card (e.g. Scale In for the group)
  const groupBox = element.boxAnimation ?? null;
  const groupExit = element.boxExitAnimation ?? null;

  return (
    <AnimatedField
      boxAnimation={groupBox}
      boxExitAnimation={groupExit}
      playing={playing}
      isPaused={isPaused}
      currentTimeSec={currentTimeSec}
    >
      <SurfaceCard compact>
        <div style={{ display: "grid", gap: "var(--space-6)" }}>
          {title && (
            <Title size="xl">
              <EditableText
                value={title}
                editing={editing}
                onChange={(v) => onChange?.("title", v)}
              />
            </Title>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gap: "var(--space-4)",
            }}
          >
            <SortableList
              editing={editing}
              items={stats}
              direction="horizontal"
              onReorder={(newStats) => onReorder?.("stats", newStats)}
              renderItem={(stat, i) => {
                const val = resolveText(stat.value);
                const unit = resolveTextOptional(stat.unit);
                const label = resolveText(stat.label);
                const detail = resolveTextOptional(stat.detail);
                const statBox = stat.boxAnimation ?? stat.value?.boxAnimation ?? null;
                const statExit = stat.boxExitAnimation ?? stat.value?.boxExitAnimation ?? null;
                return (
                  <AnimatedField
                    key={i}
                    boxAnimation={statBox}
                    boxExitAnimation={statExit}
                    playing={playing}
                    isPaused={isPaused}
                    currentTimeSec={currentTimeSec}
                  >
                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          padding: "var(--space-2)",
                          display: "grid",
                          gap: "var(--space-1)",
                        }}
                      >
                        {/* Value + unit row */}
                        <div
                          style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)" }}
                        >
                          <span
                            style={{
                              fontFamily: "var(--font-heading)",
                              fontSize: "var(--text-3xl)",
                              fontWeight: "var(--font-heading-weight)",
                              color: "var(--color-text-primary)",
                              lineHeight: 1,
                            }}
                          >
                            <EditableText
                              value={val}
                              editing={editing}
                              onChange={(v) => onChange?.(`stats.${i}.value`, v)}
                            />
                          </span>
                          {(unit !== null || editing) && (
                            <span
                              style={{
                                fontSize: "var(--text-sm)",
                                color: "var(--color-text-accent)",
                                fontFamily: "var(--font-body)",
                              }}
                            >
                              <EditableText
                                value={unit ?? ""}
                                editing={editing}
                                onChange={(v) => onChange?.(`stats.${i}.unit`, v)}
                                placeholder="unit"
                              />
                            </span>
                          )}
                        </div>
                        {/* Label */}
                        <span
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--color-text-secondary)",
                            fontFamily: "var(--font-body)",
                          }}
                        >
                          <EditableText
                            value={label}
                            editing={editing}
                            onChange={(v) => onChange?.(`stats.${i}.label`, v)}
                          />
                        </span>
                        {/* Detail */}
                        {(detail !== null || editing) && (
                          <span
                            style={{
                              fontSize: "var(--text-xs)",
                              color: "var(--color-text-muted)",
                              fontFamily: "var(--font-body)",
                            }}
                          >
                            <EditableText
                              value={detail ?? ""}
                              editing={editing}
                              onChange={(v) => onChange?.(`stats.${i}.detail`, v)}
                              placeholder="• Add detail…"
                            />
                          </span>
                        )}
                      </div>
                      {editing && (
                        <button
                          type="button"
                          aria-label="Delete stat"
                          onClick={() =>
                            onReorder?.(
                              "stats",
                              stats.filter((_, idx) => idx !== i)
                            )
                          }
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "none",
                            borderRadius: "var(--radius-sm, 4px)",
                            background: "transparent",
                            color: "var(--color-text-muted, #94a3b8)",
                            cursor: "pointer",
                            fontSize: 16,
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </AnimatedField>
                );
              }}
            />
          </div>
          {editing && (
            <button
              type="button"
              aria-label="Add stat"
              onClick={() => onReorder?.("stats", [...stats, { value: "0", label: "New metric" }])}
              style={{
                width: "100%",
                padding: "6px",
                border: "1px dashed var(--color-border-subtle, rgba(255,255,255,0.12))",
                borderRadius: "var(--radius-sm, 4px)",
                background: "transparent",
                color: "var(--color-text-muted, #94a3b8)",
                cursor: "pointer",
                fontSize: "var(--text-sm, 13px)",
                fontFamily: "var(--font-body, sans-serif)",
                textAlign: "center",
              }}
            >
              + Add stat
            </button>
          )}
        </div>
      </SurfaceCard>
    </AnimatedField>
  );
}

export default StatGroupRenderer;
